import type { ZodType } from 'zod';

import { z } from 'zod';

import { countryCodes, europeanUnionCountryCodes, europeanUnionVatCountryCodes, vatCountryCodes } from '~/constants/countries';
import { includes } from '~/utils/arrays';

// API responses

export const apiResourceSchema = <T extends ZodType>(dataSchema: T) =>
  z.object({
    data: dataSchema,
  });

export const paginatedApiResourceSchema = <T extends ZodType>(dataSchema: T) =>
  z.object({
    data: dataSchema,
    pagination: z.object({
      currentPage: z.number(),
      nextPage: z.number().nullable(),
      totalResults: z.number(),
    }),
  });

// Common

/** @deprecated use `z.iso.datetime({ offset: true })` instead, in combintation with `->toIso8601String()` in the backend */
export const iso8601DateTimeSchema = z.string().refine((value) => /^\d{4}-\d{2}-\d{2}(?: \d{2}:\d{2}:\d{2})?$/.test(value));

export const countryCodeSchema = z.custom<(typeof countryCodes)[number]>((value) => includes(countryCodes, value));
export const euCountryCodeSchema = z.custom<(typeof europeanUnionCountryCodes)[number]>((value) =>
  includes(europeanUnionCountryCodes, value),
);
export const vatCountryCodeSchema = z.custom<(typeof vatCountryCodes)[number]>((value) => includes(vatCountryCodes, value));
export const euVatCountryCodeSchema = z.custom<(typeof europeanUnionVatCountryCodes)[number]>((value) =>
  includes(europeanUnionVatCountryCodes, value),
);

// CO and FC documents

export const transportTypeSchema = z.enum(['Peppol', 'Email', 'EmailFallbackForPeppol']);
export const peppolStatusSchema = z.enum(['NotSent', 'Sent', 'Delivered', 'Accepted', 'Refused']);

// CO documents

export const paymentTermSchema = z.union([z.literal(15), z.literal(30), z.literal(45), z.literal(60)]);
export const activityTypeSchema = z.enum(['CreativeGeneral', 'CreativeSubcontracting', 'CreativeComputer', 'CreativeReuse', 'NonCreative']);
export const lineDiscountTypeSchema = z.enum(['percentage', 'flatRate']);
export const lineUnitSchema = z.enum(['hours', 'days', 'halfDays', 'kilometers', 'words', 'characters']);
export const productStatusSchema = z.enum(['InReview', 'Approved', 'Rejected']);

export const entryLineSchema = z.object({
  uuid: z.string(),
  type: z.literal('entry'),
  productId: z.number().nullable(),
  productName: z.string().nullable(),
  productStatus: productStatusSchema.nullable(),
  description: z.string(),
  price: z.number(),
  discountType: lineDiscountTypeSchema.nullable(),
  discountValue: z.coerce.number().nullable(),
  quantity: z.number(),
  unit: lineUnitSchema.nullable(),
  activityType: activityTypeSchema,
  isPhotoAlbum: z.boolean(),
});

export const textLineSchema = z.object({
  uuid: z.string(),
  type: z.literal('text'),
  description: z.string(),
  isPhotoAlbum: z.boolean(),
});

export const lineSchema = z.discriminatedUnion('type', [entryLineSchema, textLineSchema]);

const vatAmountsSchema = z.tuple([
  z.object({ percentage: z.literal(6), amount: z.number() }),
  z.object({ percentage: z.literal(21), amount: z.number() }),
]);

export const calculationDataSchema = z.object({
  coExclVat: z.number(),
  coVat: z.number(),
  coInclVat: z.number(),
  coVatAmounts: vatAmountsSchema,
  coDiscount: z.number(),
  isMinimumCommissionApplied: z.boolean(),

  fcExclVat: z.number(),
  fcPerformanceExclVat: z.number(),
  fcCopyrightExclVat: z.number(),
  fcVatAmounts: vatAmountsSchema,
  fcWithholdingTax: z.number(),

  // Legacy invoices and quotation (before the new calculation) don't have
  // advantage calculations, hence it is marked as nullable.
  advantageWithCs: z
    .object({
      advantage: z.number(),
      coTotalCommission: z.number(),
      fcCopyrightWithholdingTax: z.number(),
      fcCopyrightExpenses: z.number(),
      fcPerformanceExpenses: z.number(),
      fcPerformancePersonalTaxes: z.number(),
      fcPerformanceSocialContribution: z.number(),
      netAmount: z.number(),
    })
    .nullable(),
  advantageWithoutCs: z
    .object({
      expenses: z.number(),
      netAmount: z.number(),
      personalTaxes: z.number(),
      profit: z.number(),
      socialContribution: z.number(),
    })
    .nullable(),

  userIsWorkingFullTime: z.boolean(),
  clientIsExemptFromVat: z.boolean(),
});

export const documentYearQuarterSchema = z.custom<`${number}-${number}`>((value) => typeof value === 'string' && /\d{4}-\d/.test(value));

export const previewHtmlSchema = z.object({
  html: z.string(),
});
